import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type DepositReversalStatus = 'pending' | 'approved' | 'rejected';

export interface DepositReversal {
  id: string;
  deposit_request_id: string;
  ledger_entry_id: string | null;
  user_id: string;
  client_email: string | null;
  product_name: string;
  deposit_reference_number: string | null;
  amount: string;
  currency_code: string;
  reference_number: string;
  reason: string | null;
  status: DepositReversalStatus;
  proposed_by: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  version: number;
  created_at: string;
}

export interface DepositReversalListResponse {
  items: DepositReversal[];
  total: number;
  page: number;
  size: number;
}

/** Deposit reversals (`/api/v1/deposit-reversals`): the money-IN mirror of
 *  withdrawal reversals — an approved deposit whose wire the bank returned. */
class DepositReversalsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async list(status: DepositReversalStatus[] = [], size = 100): Promise<DepositReversalListResponse> {
    const params = new URLSearchParams();
    for (const s of status) params.append('status', s);
    params.set('size', String(size));
    const res = await this.api.get<DepositReversalListResponse>(
      `/api/v1/deposit-reversals?${params.toString()}`,
    );
    return res.data;
  }

  async propose(depositRequestId: string, reason: string): Promise<{ reversal_id: string }> {
    const res = await this.api.post<{ reversal_id: string }>(
      '/api/v1/deposit-reversals',
      { deposit_request_id: depositRequestId, reason },
      { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    );
    return res.data;
  }

  async approve(id: string, expectedVersion: number, mfaCode: string): Promise<{ ledger_entry_id: string }> {
    const res = await this.api.post<{ reversal_id: string; ledger_entry_id: string }>(
      `/api/v1/deposit-reversals/${id}/approve`,
      { expected_version: expectedVersion, mfa_code: mfaCode },
    );
    return res.data;
  }

  async reject(id: string, expectedVersion: number, reason: string): Promise<void> {
    await this.api.post(`/api/v1/deposit-reversals/${id}/reject`, {
      expected_version: expectedVersion,
      reason,
    });
  }
}

export default new DepositReversalsService();
